import React, {
  Suspense,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useState } from "react";
import { TabContent, TabPane } from "reactstrap";
import Loader from "components/Loader";
import styles from "./OfficeStaffInformation.module.scss";
import { withTranslation } from "react-i18next";
import constants from "../../../constants";
import { getStorage, setStorage } from "utils";
import checkIcon from "assets/images/ico-check.svg";
import {
  useGetGlobalOfficeStaffInformationList,
  useGetOfficesInformationList,
} from "repositories/office-repository";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import SearchBar from "../Notes/component/Filters/SearchBar";
import CustomDropdown from "components/Dropdown";
import { debounce } from "lodash";
import StaffCardView from "./components/StaffCardView";
import SideBarWrapper from "./components/SideBarWrapper";
import PageWrapperStaffInformation from "./components/PageWrapperStaffInformation";
import OfficeCardView from "./components/OfficeCardView";
const PAGE_SIZE = 10;

const defaultSortBy = {
  sortBy: 1,
  SortDescending: false,
  value: "nameAscending",
};
const cacheTabName = constants.notes.cache.globalOfficeStaffInformationTabs;
const GlobalOfficeStaffInformation = ({ t }) => {
  const cachedActiveTab = getStorage(cacheTabName);
  const { selectedId, commonOwnerData, isStaff } =
    useAccountOwnerCommon();
  const [activeTab, setActiveTab] = useState(cachedActiveTab?.activeTab ?? 1);
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState(
    cachedActiveTab?.sortBy ?? defaultSortBy
  );
  const [pageNo, setPageNo] = useState(1);
  const [selectedOffice, setSelectedOffice] = useState(
    cachedActiveTab?.offices ?? []
  );
  const [selectedRole, setSelectedRole] = useState(
    cachedActiveTab?.roles ?? []
  );
  const [isSidebarActive, setSidebarActive] = useState(true);
  const [officeListShow, setOfficeListShow] = useState(null);

  const tabName = constants.officeStaffInformation;
  const officeTabNotActive = activeTab !== tabName.office;
  const {
    data: officeDataList,
    isLoading: officeDataLoading,
    isFetching: officeDataisFetching,
  } = useGetOfficesInformationList(
    {
      officeIds: selectedOffice,
      ownerId: selectedId,
      pageNumber: pageNo,
      pageSize: 100,
      SearchTerm: searchValue,
      sortBy: sortBy.sortBy,
      SortDescending: sortBy.SortDescending,
    },
    {
      enabled: !officeTabNotActive,
    }
  );

  const { data, isLoading, isRefetching } =
    useGetGlobalOfficeStaffInformationList(
      {
        designationIds: selectedRole,
        officeIds: selectedOffice,
        ownerId: selectedId,
        pageNumber: pageNo,
        pageSize: PAGE_SIZE,
        isAdminOnly: activeTab === tabName.ownerManager,
        SearchTerm: searchValue,
        sortBy: sortBy.sortBy,
        SortDescending: sortBy.SortDescending,
      },
      { enabled: officeTabNotActive && !!selectedId }
    );

  const handleChange = useCallback((event) => {
    setSearchValue(event.target.value.trim());
    setPageNo(1);
  }, []);
  const debouncedSearch = useCallback(debounce(handleChange, 500), []);
  const searchRef = useRef(null);
  const resetFilter = useCallback(() => {
    setSelectedOffice([]);
    setSelectedRole([]);
    setStorage(cacheTabName, {
      activeTab,
      sortBy,
      roles: [],
      offices: [],
    });
  }, [activeTab, sortBy]);
  const manageTab = useCallback(
    (tab) => {
      window.scrollTo(0, 0);
      setPageNo(1);
      setActiveTab(tab);
      setStorage(cacheTabName, {
        ...cachedActiveTab,
        activeTab: tab,
      });
      handleChange({ target: { value: "" } });
      searchRef.current.value = "";
    },
    [cachedActiveTab, handleChange]
  );
  const staffData = useMemo(() => data?.data || [], [data?.data]);

  const selecteId = useCallback((id) => {
    switch (id) {
      case "nameDescending":
        return {
          sortBy: 1,
          SortDescending: true,
        };
      case "createdAscending":
        return {
          sortBy: 2,
          SortDescending: false,
        };
      case "createdDescending":
        return {
          sortBy: 2,
          SortDescending: true,
        };

      default:
        return {
          sortBy: 1,
          SortDescending: false,
        };
    }
  }, []);
  const officeLength = officeDataList?.data?.length > 1;
  return (
    <PageWrapperStaffInformation
      title={t("navbar.companyDirectory")}
      activeTab={activeTab}
      tabName={tabName}
      manageTab={manageTab}
      switchOwner={
        isStaff && (
          <div className="show-owner-name-text marginBottom30">
              {t("accountOwner.ownerAdminHeadingForCompanyDirectory")}{" "}
              <span>
                '{commonOwnerData?.firstName} {commonOwnerData?.lastName}'
              </span>
          </div>
        )
      }
    >
      <SideBarWrapper
        handleSidebarToggle={() => {
          setSidebarActive(!isSidebarActive);
        }}
        resetFilter={resetFilter}
        applyFilter={(selectedOfficeFilter, selectedRoles) => {
          setSelectedOffice(selectedOfficeFilter);
          setSelectedRole(selectedRoles);
          setStorage(cacheTabName, {
            ...cachedActiveTab,
            roles: selectedRoles,
            offices: selectedOfficeFilter,
          });
        }}
        isSidebarActive={isSidebarActive}
        officeTabNotActive={officeTabNotActive}
        setOfficeListShow={setOfficeListShow}
        cacheOffices={cachedActiveTab?.offices}
        cacheRoles={cachedActiveTab?.roles}
      >
        <div className={styles["staff-search-tabs"]}>
          <SearchBar
            inputRef={searchRef}
            handleSearchTerm={debouncedSearch}
            t={t}
            placeholder={t(
              `accountOwner.searchBy${officeTabNotActive ? "" : "Office"}Name`
            )}
          />
          <CustomDropdown
            options={[
              { id: "nameAscending", name: "Name (A-Z)" },
              { id: "nameDescending", name: "Name (Z-A)" },
              { id: "createdAscending", name: "Created (A-Z)" },
              { id: "createdDescending", name: "Created (Z-A)" },
            ]}
            selectedOption={0}
            selectOption={(id) => {
              const values = selecteId(id);
              values.value = id;
              setStorage(cacheTabName, {
                ...cachedActiveTab,
                sortBy: values,
              });
              setSortBy(values);
            }}
            defaultValue="Sort By"
            renderAnyThing={(id) => {
              if (sortBy.value === id) {
                return <img src={checkIcon} alt="icon" className="mr-2" />;
              }
              return <span className="d-inline me-md" />;
            }}
          />
        </div>
        <TabContent activeTab={activeTab}>
          <Suspense fallback={<Loader />}>
            <TabPane tabId={1}>
              <StaffCardView
                message={t("accountOwner.noOfficeFound")}
                staffData={officeDataList?.data}
                isLoading={officeDataLoading || officeDataisFetching}
              >
                {officeDataList?.data?.map((officeDetail) => (
                  <OfficeCardView
                    office={officeDetail}
                    key={officeDetail.id}
                    officeLength={officeLength}
                  />
                ))}
              </StaffCardView>
            </TabPane>
            <TabPane tabId={2}>
              <StaffCardView
                message={
                  staffData?.length
                    ? undefined
                    : t("accountOwner.noOwnerManagerFound")
                }
                isLoading={isLoading || isRefetching}
                staffData={staffData}
                officeListShow={officeListShow}
              />
            </TabPane>
            <TabPane tabId={3}>
              <StaffCardView
                isLoading={isLoading || isRefetching}
                staffData={staffData}
                officeListShow={officeListShow}
              />
            </TabPane>
          </Suspense>
        </TabContent>
      </SideBarWrapper>
    </PageWrapperStaffInformation>
  );
};
export default withTranslation()(GlobalOfficeStaffInformation);
